import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const LEEG = {
  sku: "", naam: "", ean: "", merk: "", leverancier: "",
  eenheid: "stuk", btw_tarief: "21", inkoopprijs: "", verkoopprijs: "",
};

export function NieuwProductDialog({ onCreated }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState(LEEG);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const num = (v) => {
    const n = parseFloat(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  };

  const opslaan = async () => {
    if (!f.sku.trim() || !f.naam.trim()) {
      toast.error("SKU en naam zijn verplicht");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        sku: f.sku.trim(),
        naam: f.naam.trim(),
        ean: f.ean.trim(),
        merk: f.merk.trim(),
        leverancier: f.leverancier.trim(),
        eenheid: f.eenheid,
        btw_tarief: f.btw_tarief,
        inkoopprijs: num(f.inkoopprijs),
        verkoopprijs: num(f.verkoopprijs),
      };
      const { data } = await api.post("/producten", payload);
      toast.success("Product aangemaakt");
      setOpen(false);
      setF(LEEG);
      onCreated?.();
      if (data?.id) navigate(`/producten/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aanmaken mislukt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setF(LEEG); }}>
      <DialogTrigger asChild>
        <Button data-testid="nieuw-product-btn">
          <Plus className="h-4 w-4 mr-1" /> Nieuw product
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="nieuw-product-dialog">
        <DialogHeader>
          <DialogTitle>Nieuw product toevoegen</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="np-sku">SKU <span className="text-destructive">*</span></Label>
              <Input id="np-sku" value={f.sku} onChange={(e) => set("sku", e.target.value)}
                placeholder="NB1099" data-testid="np-sku" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="np-ean">EAN</Label>
              <Input id="np-ean" value={f.ean} onChange={(e) => set("ean", e.target.value)}
                placeholder="8719689…" data-testid="np-ean" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="np-naam">Naam <span className="text-destructive">*</span></Label>
            <Input id="np-naam" value={f.naam} onChange={(e) => set("naam", e.target.value)}
              placeholder="Productnaam" data-testid="np-naam" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="np-merk">Merk</Label>
              <Input id="np-merk" value={f.merk} onChange={(e) => set("merk", e.target.value)}
                placeholder="NextBrush / Frankly" data-testid="np-merk" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="np-leverancier">Leverancier</Label>
              <Input id="np-leverancier" value={f.leverancier} onChange={(e) => set("leverancier", e.target.value)}
                placeholder="Naam leverancier" data-testid="np-leverancier" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="np-inkoop">Inkoopprijs (€)</Label>
              <Input id="np-inkoop" inputMode="decimal" value={f.inkoopprijs}
                onChange={(e) => set("inkoopprijs", e.target.value)} placeholder="0,00" data-testid="np-inkoopprijs" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="np-verkoop">Verkoopprijs (€)</Label>
              <Input id="np-verkoop" inputMode="decimal" value={f.verkoopprijs}
                onChange={(e) => set("verkoopprijs", e.target.value)} placeholder="0,00" data-testid="np-verkoopprijs" />
            </div>
            <div className="space-y-1.5">
              <Label>BTW</Label>
              <Select value={f.btw_tarief} onValueChange={(v) => set("btw_tarief", v)}>
                <SelectTrigger data-testid="np-btw"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="21">21%</SelectItem>
                  <SelectItem value="9">9%</SelectItem>
                  <SelectItem value="0">0%</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5 w-1/2">
            <Label>Eenheid</Label>
            <Select value={f.eenheid} onValueChange={(v) => set("eenheid", v)}>
              <SelectTrigger data-testid="np-eenheid"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="stuk">Stuk</SelectItem>
                <SelectItem value="doos">Doos</SelectItem>
                <SelectItem value="kg">Kg</SelectItem>
                <SelectItem value="liter">Liter</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <p className="text-xs text-muted-foreground">
            Je kunt na het aanmaken alle overige gegevens (voorraad, teksten, foto's, landprijzen) op de productpagina invullen.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
          <Button onClick={opslaan} disabled={saving} data-testid="np-opslaan">
            {saving ? "Bezig…" : "Aanmaken"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
